import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrencyValue = (value) => {
  const numericValue = Number(value || 0);
  return `₹${currencyFormatter.format(numericValue)}`;
};

const formatDisplayValue = (value, column = {}) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (column.type === 'currency') {
    return formatCurrencyValue(value);
  }

  if (column.type === 'number') {
    return Number(value || 0).toLocaleString('en-IN');
  }

  return String(value);
};

const normalizeFilename = (title) => `${(title || 'report').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-report`;

export const exportReportToExcel = ({
  title,
  data = [],
  columns = [],
  summary = [],
  filename,
}) => {
  const rows = data.map((row) => {
    const mapped = {};
    columns.forEach((column) => {
      mapped[column.label] = formatDisplayValue(row[column.key], column);
    });
    return mapped;
  });

  const summaryRows = summary.map((item) => ({
    Label: item.label,
    Value: item.value,
  }));

  const workbook = XLSX.utils.book_new();
  const sheet = XLSX.utils.json_to_sheet(rows);
  const summarySheet = XLSX.utils.json_to_sheet(summaryRows);

  XLSX.utils.book_append_sheet(workbook, sheet, 'Report');
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  const finalFilename = `${filename || normalizeFilename(title)}.xlsx`;
  XLSX.writeFile(workbook, finalFilename);
};

export const exportReportToPdf = ({
  title,
  data = [],
  columns = [],
  summary = [],
  reportPeriod,
  branchLabel,
  filters = {},
  filename,
}) => {
  const doc = new jsPDF({ orientation: 'landscape' });
  const pageTitle = title || 'Report';
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(pageTitle, 14, 18);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const metaLines = [
    reportPeriod ? `Period: ${reportPeriod}` : null,
    branchLabel ? `Branch / Outlet: ${branchLabel}` : null,
    Object.keys(filters).length ? `Filters: ${Object.entries(filters).map(([key, value]) => `${key}: ${value}`).join(' | ')}` : null,
  ].filter(Boolean);

  metaLines.forEach((line, index) => {
    doc.text(line, 14, 30 + index * 7);
  });

  const bodyRows = data.map((row) => columns.map((column) => formatDisplayValue(row[column.key], column)));
  const headerRow = columns.map((column) => column.label);

  autoTable(doc, {
    head: [headerRow],
    body: bodyRows,
    startY: metaLines.length ? 28 + metaLines.length * 7 : 34,
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 3 },
    headStyles: { fillColor: [30, 64, 175], textColor: [255, 255, 255], fontStyle: 'bold' },
    margin: { left: 14, right: 14 },
    tableWidth: 'wrap',
    didDrawPage: (data) => {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Page ${data.pageNumber}`, pageWidth - 22, 10, { align: 'right' });
    },
  });

  if (summary.length) {
    const summaryY = doc.lastAutoTable.finalY + 12;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 14, summaryY);

    doc.setFont('helvetica', 'normal');
    summary.forEach((item, index) => {
      doc.text(`${item.label}: ${item.value}`, 14, summaryY + 8 + index * 7);
    });
  }

  const finalFilename = `${filename || normalizeFilename(title)}.pdf`;
  doc.save(finalFilename);
};

export const printReport = ({
  title,
  data = [],
  columns = [],
  summary = [],
  reportPeriod,
  branchLabel,
  filters = {},
}) => {
  const win = window.open('', '_blank', 'width=1200,height=900');
  if (!win) {
    return;
  }

  const buildSummaryRows = (items) => items
    .map((item) => `<tr><th style="text-align:left; padding:8px 12px; border:1px solid #d1d5db; background:#f8fafc;">${item.label}</th><td style="padding:8px 12px; border:1px solid #d1d5db; text-align:right; font-weight:600;">${item.value}</td></tr>`)
    .join('');

  const buildTableRows = () => data.map((row) => `
    <tr>
      ${columns.map((column) => `<td style="padding:8px 10px; border:1px solid #d1d5db; text-align:${column.type === 'currency' || column.type === 'number' ? 'right' : 'left'};">${formatDisplayValue(row[column.key], column)}</td>`).join('')}
    </tr>
  `).join('');

  const tableHeader = columns.map((column) => `<th style="padding:8px 10px; border:1px solid #d1d5db; background:#1d4ed8; color:#ffffff; text-align:left; font-weight:700;">${column.label}</th>`).join('');
  const summaryMarkup = summary.length ? `
    <table style="width:100%; border-collapse:collapse; margin-top:20px; font-size:12px;">
      <tbody>${buildSummaryRows(summary)}</tbody>
    </table>
  ` : '';

  const filterMarkup = Object.keys(filters).length ? `<div style="margin-top:12px; font-size:12px; color:#334155;">${Object.entries(filters).map(([key, value]) => `<span style="margin-right:14px;"><strong>${key}:</strong> ${value}</span>`).join('')}</div>` : '';

  const pageHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          @page { size: A4 portrait; margin: 14mm; }
          body { font-family: Arial, sans-serif; color: #0f172a; padding: 20px; }
          h1 { font-size: 22px; margin: 0 0 12px; }
          .meta { font-size: 12px; color: #475569; margin-bottom: 18px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { border: 1px solid #d1d5db; padding: 8px; }
          .summary-table { margin-top: 18px; }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div class="meta">
          ${reportPeriod ? `<div><strong>Period:</strong> ${reportPeriod}</div>` : ''}
          ${branchLabel ? `<div><strong>Branch / Outlet:</strong> ${branchLabel}</div>` : ''}
          ${filterMarkup}
        </div>
        <table>
          <thead>
            <tr>${tableHeader}</tr>
          </thead>
          <tbody>${buildTableRows()}</tbody>
        </table>
        ${summaryMarkup}
      </body>
    </html>
  `;

  win.document.write(pageHtml);
  win.document.close();

  setTimeout(() => {
    win.focus();
    win.print();
    setTimeout(() => win.close(), 500);
  }, 300);
};
